/**
 * Өрсөлдөөний эрэмбэ — дашбоардын блок.
 *
 * ⚠️ САЛБАРУУД БАГААРАА өрсөлдөнө. Салбарын мөрийг дэлгэхэд ажилтны
 *    ХУВЬ НЭМЭР харагдана — ГЭХДЭЭ дугааргүй, зэрэглэлгүй, нэрийн
 *    цагаан толгойн дарааллаар. «Шилдэг ажилтан» гэж зарлахгүй.
 *
 * ⚠️ Таб нь `/leaderboard/competitions`-оос ирнэ. Шинэ өрсөлдөөн
 *    бүртгэгдэхэд энэ файл өөрчлөгдөхгүй — таб өөрөө нэмэгдэнэ.
 *
 * ⚠️ Оноо string-ээр ирнэ (Decimal). `Number()` руу зөвхөн харьцуулах,
 *    зурахад хөрвүүлнэ.
 */

import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { formatMntShort, formatQty } from '@boots/domain';
import { api, type Envelope } from '../../lib/api';
import { useQuery } from '@tanstack/react-query';
import type { CompetitionInfo, LeaderboardEntry, LeaderboardReport } from '../../lib/types';
import { Section } from '../section';
import { EmptyState, ErrorState } from '../empty-state';
import { Skeleton } from '../skeleton';
import { Tabs } from '../ui';
import { Podium } from './leaderboard-podium';
import { LeaderRow } from './leaderboard-row';

/**
 * Өмнөх хугацааны түлхүүр — серверийн `prevPeriodKey`-тэй ИЖИЛ дүрэм
 * (`services/core/src/modules/leaderboard.ts`). Хоёр талд давхардаж
 * байгаа нь мэдээжийн муу тал, гэхдээ энэ мэдлэгийг эндпойнтоор
 * дамжуулах нь хариуны бүтцийг өөрчлөх учиртай — тэр нь илүү өртөгтэй.
 */
function prevPeriodKey(type: string, key: string): string {
  if (type === 'year') return String(Number(key) - 1);
  if (type === 'quarter') {
    const [y, q] = key.split('-Q').map(Number);
    return Number(q) === 1 ? `${Number(y) - 1}-Q4` : `${y}-Q${Number(q) - 1}`;
  }
  const [y, m] = key.split('-').map(Number);
  return Number(m) === 1
    ? `${Number(y) - 1}-12`
    : `${y}-${String(Number(m) - 1).padStart(2, '0')}`;
}

const PERIODS = [
  { key: 'month', label: 'Сар' },
  { key: 'quarter', label: 'Улирал' },
  { key: 'year', label: 'Жил' },
];

export function LeaderboardCard({ myStoreId }: { myStoreId: string | null }) {
  const [params, setParams] = useSearchParams();
  const [open, setOpen] = useState<string | null>(null);

  const competition = params.get('competition') ?? 'branch';
  const period = params.get('period') ?? 'month';

  const comps = useQuery({
    queryKey: ['competitions'],
    queryFn: () => api<Envelope<CompetitionInfo[]>>('/leaderboard/competitions'),
    staleTime: 30 * 60_000,
  });

  const board = useQuery({
    queryKey: ['leaderboard', competition, period],
    queryFn: () =>
      api<Envelope<LeaderboardReport>>(
        `/leaderboard?competition=${competition}&period=${period}`,
      ),
    staleTime: 60_000,
  });

  /*
    ӨМНӨХ ХУГАЦААНЫ ЭРЭМБЭ — хоёр асуултад хариулна:
      · «Би өөрөөсөө хэр сайжирсан бэ» — өөрийн өмнөх оноо
      · «Өнгөрсөн удаагийн ШИЛДГЭЭС хэр байна вэ» — тэр үеийн 1-р байр

    ⚠️ Түлхүүрийг сервер өөрөө хэлнэ (`period.key`). Огноог клиент дээр
       тоолж эхлэвэл улирал, оны хил дээр зөрнө.
    ⚠️ Өмнөх хугацаанд өгөгдөл байхгүй байж болно (шинэ салбар, эхний
       сар) — тэр үед блок унахгүй, зүгээр л харьцуулалт гарахгүй.
  */
  const prevKey = board.data ? prevPeriodKey(period, board.data.data.period.key) : null;
  const prevBoard = useQuery({
    queryKey: ['leaderboard', competition, period, 'prev', prevKey],
    queryFn: () =>
      api<Envelope<LeaderboardReport>>(
        `/leaderboard?competition=${competition}&period=${period}&key=${prevKey}`,
      ),
    enabled: !!prevKey,
    staleTime: 5 * 60_000,
    retry: false,
  });

  const prevScores = new Map(
    (prevBoard.data?.data.entries ?? []).map((e) => [e.subject.id, Number(e.totalScore)]),
  );
  const prevTop = prevBoard.data?.data.entries[0];

  const patch = (kv: Record<string, string>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(kv)) next.set(k, v);
    setParams(next, { replace: true });
    setOpen(null);
  };

  const r = board.data?.data;
  const tabs = (comps.data?.data ?? []).map((c) => ({ key: c.key, label: c.label }));
  const isBranch = r?.subjectType === 'BRANCH';

  return (
    <Section
      feature
      title="Өрсөлдөөн"
      subtitle={r?.period.label}
      note={r?.isFinalized ? 'хөлдсөн үр дүн' : undefined}
      actions={
        <div className="flex flex-wrap items-center gap-2">
          {tabs.length > 0 ? (
            <Tabs tabs={tabs} value={competition} onChange={(k) => patch({ competition: k })} />
          ) : null}
          <Tabs tabs={PERIODS} value={period} onChange={(k) => patch({ period: k })} />
        </div>
      }
    >
      {board.error ? (
        <ErrorState
          message={(board.error as Error).message}
          onRetry={() => void board.refetch()}
        />
      ) : board.isPending ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-[104px] w-full rounded-well" />
          {Array.from({ length: 4 }, (_, i) => (
            <Skeleton key={i} className="h-9 w-full" />
          ))}
        </div>
      ) : !r || r.entries.length === 0 ? (
        <EmptyState
          compact
          title="Энэ хугацаанд өрсөлдөөний өгөгдөл алга"
          description="Борлуулалт, хүлээн авалт бүртгэгдмэгц эрэмбэ гарч эхэлнэ."
        />
      ) : (
        <div className="flex flex-col gap-3">
          <Podium entries={r.entries} isBranch={isBranch} myStoreId={myStoreId} />

          <ul className="flex flex-col">
            {r.entries.map((e) => (
              <LeaderRow
                key={e.subject.id}
                entry={e}
                prevScore={prevScores.get(e.subject.id) ?? null}
                prevTop={
                  prevTop ? { name: prevTop.subject.name, score: Number(prevTop.totalScore) } : null
                }
                prevLabel={prevBoard.data?.data.period.label ?? null}
                isBranch={isBranch}
                mine={isBranch && e.subject.id === myStoreId}
                open={open === e.subject.id}
                onToggle={() => setOpen(open === e.subject.id ? null : e.subject.id)}
                competition={competition}
                period={period}
              />
            ))}
          </ul>

          <p className="text-label text-ink-3">
            {isBranch
              ? 'Салбарууд багаараа өрсөлдөнө. Эрэмбэ нь зорилтын биелэлт, өсөлт, нэг ажилтанд ногдох борлуулалт зэрэг нормчилсон үзүүлэлтээр гарна — нийт дүн ганцаараа шийдэхгүй.'
              : 'Хувь хүний эрэмбэ. Олон цуглуулж цөөн алдаа гаргасан нь дээгүүр бичигдэнэ.'}
          </p>
        </div>
      )}
    </Section>
  );
}

/** Топ-3-ын дүнг товч бичихэд хэрэглэнэ. */
export function headlineOf(e: LeaderboardEntry, isBranch: boolean): string {
  if (isBranch) return formatMntShort(e.meta?.revenueMnt ?? '0');
  const units = e.breakdown.find((b) => b.metric === 'collectedUnits')?.raw;
  const labeled = e.breakdown.find((b) => b.metric === 'labeledUnits')?.raw;
  return `${formatQty(units ?? labeled ?? 0)} ш`;
}
